// BracketConnectors: renders CSS-based connector lines between bracket columns.
// Each "connector pair" shows two upstream slots merging into one downstream slot.
// Lines use border-border by default; filled paths use border-accent.

interface ConnectorPair {
  topFilled?: boolean;  // whether the top upstream slot has a pick flowing through
  bottomFilled?: boolean; // whether the bottom upstream slot has a pick flowing through
}

interface ConnectorProps {
  direction: 'converge-right' | 'converge-left';
  pairs: ConnectorPair[];
}

// A single pair connector: two horizontal lines from upstream slots meet at a vertical
// bar, then a horizontal line continues to the downstream slot.
// For 'converge-right': upstream is on the right, downstream is on the left (West side RTL)
// For 'converge-left': upstream is on the left, downstream is on the right (East side RTL)
const ConnectorPairSegment = ({
  topFilled,
  bottomFilled,
  direction,
}: ConnectorPair & { direction: 'converge-right' | 'converge-left' }) => {
  const isRight = direction === 'converge-right';

  const topColor = topFilled ? 'bg-accent shadow-[0_0_3px_rgba(234,88,12,0.5)]' : 'bg-border';
  const bottomColor = bottomFilled ? 'bg-accent shadow-[0_0_3px_rgba(234,88,12,0.5)]' : 'bg-border';
  const verticalColor = topFilled && bottomFilled ? 'bg-accent shadow-[0_0_3px_rgba(234,88,12,0.5)]' : 'bg-border';
  const outColor = topFilled || bottomFilled ? 'bg-accent shadow-[0_0_3px_rgba(234,88,12,0.5)]' : 'bg-border';

  return (
    <div className="relative flex-1 flex flex-col">
      {/* Top half: horizontal stub from top upstream slot */}
      <div className="flex-1 flex items-center relative">
        {/* Horizontal line from upstream */}
        <div
          className={`absolute top-1/2 h-[2px] w-full ${topColor}`}
          style={isRight ? { left: 0, right: '50%' } : { right: 0, left: '50%' }}
        />
      </div>

      {/* Center vertical bar connecting top and bottom — spans only between the two stubs */}
      <div
        className={`absolute w-[2px] ${verticalColor}`}
        style={{ top: '25%', bottom: '25%', ...(isRight ? { right: '50%' } : { left: '50%' }) }}
      />

      {/* Bottom half: horizontal stub from bottom upstream slot */}
      <div className="flex-1 flex items-center relative">
        {/* Horizontal line from upstream */}
        <div
          className={`absolute top-1/2 h-[2px] w-full ${bottomColor}`}
          style={isRight ? { left: 0, right: '50%' } : { right: 0, left: '50%' }}
        />
      </div>

      {/* Outgoing horizontal line to downstream slot (from midpoint) */}
      <div
        className={`absolute top-1/2 h-[2px] w-1/2 ${outColor}`}
        style={isRight ? { left: '50%' } : { right: '50%' }}
      />
    </div>
  );
};

const BracketConnectors = ({ direction, pairs }: ConnectorProps) => {
  return (
    <div className="flex flex-col w-4 shrink-0 self-stretch">
      {pairs.map((pair, idx) => (
        <ConnectorPairSegment
          key={idx}
          direction={direction}
          topFilled={pair.topFilled}
          bottomFilled={pair.bottomFilled}
        />
      ))}
    </div>
  );
};

export default BracketConnectors;
